"use client";

import { cn } from "../../lib/utils";
import type { ZierVariante } from "./zier-varianten";

/**
 * Die Zier unter der Wortmarke — eine SPUR, und auf Wunsch eine Linie.
 *
 * ── WARUM EINE SPUR UND NICHT EINE LINIE ────────────────────────────────────
 *
 * Unter der Wortmarke stand bis 0.455.0 eine Haarlinie ueber die volle Breite.
 * Sie ist damals raus, und der Grund war geometrisch: unter „umklammert" liegt
 * die Inhaltskarte 12 px tiefer als die Leiste — eine Linie am Leistenkopf traf
 * drueben KEINE Kante und behauptete eine Ausrichtung, die es nicht gibt.
 *
 * Eine Regel spannt einen Container auf und verspricht damit, dass an ihrem
 * Ende etwas beginnt. Eine Spur hat einen Ursprung und ein Ende: sie kommt aus
 * dem Bildschirmrand, laeuft unter dem Namen durch und hoert auf. Sie behauptet
 * nichts — sie ist Herkunft. Betreiber, 2026-09-18: „keine normale Linie,
 * sondern so richtig geil."
 *
 * ── UND WARUM DIE LINIE TROTZDEM WIEDER ANGEBOTEN WIRD ──────────────────────
 *
 * Betreiber, 2026-09-19: „vielleicht gibt es die normale Linie wieder, so wie
 * wir's im Content-Bereich haben […] das wär cool, dort auch wieder die
 * Moeglichkeit zu geben, wenn ein Nutzer das mag."
 *
 * Die Begruendung oben bleibt richtig — sie gilt aber fuer WENIGER Faelle, als
 * sie bisher behauptet hat. Am 2026-09-19 am laufenden Showroom gemessen, alle
 * Werte sind Bildpunktzeilen des Fensters (`getBoundingClientRect().bottom`):
 *
 *                 Leistenkopf   Inhaltskopf   rechtes Panel
 *     flach            56            56            56        alle drei gleich
 *     schwebend        69            69            69        alle drei gleich
 *     umklammert       56            68            56        NUR der Inhalt tiefer
 *
 * Die Linie trifft also in zwei von drei Schalen ALLE Nachbarkanten, und selbst
 * unter „umklammert" trifft sie die Kopfkante des rechten Panels auf den
 * Bildpunkt genau. Was dort nicht passt, ist einzig die Inhaltskarte, und das
 * ist eine Eigenschaft DIESER Schale, kein Argument gegen die Wahl. Deshalb ist
 * sie eine Variante wie jede andere — und deshalb steht diese Einschraenkung
 * hier als Kommentar, statt geloescht zu werden.
 *
 * ── DIE LINIE SITZT AUF DER KANTE, NICHT IM ZIERBAND ────────────────────────
 *
 * Der Kopfblock der Leiste ist `h-12 md:h-14` mit `border-b border-transparent`
 * (`Sidebar.tsx`) — die Kante ist also da, sie ist nur unsichtbar. Das Zierband
 * liegt INNEN auf ihrer Oberkante: `Sidebar` haengt die Zier in ein
 * `absolute inset-x-0 bottom-0`, das SVG ist 14 hoch und endet damit bei Zeile
 * 54. Die echte Kantenzeile ist 55.
 *
 * Eine Linie im SVG laege also EINE Zeile zu hoch — und unter „flach" stossen
 * Leiste und Inhalt ohne Rinne aneinander, dort saehe man den Versatz als
 * Stufe. `Kantenlinie` ist deshalb kein SVG-Pfad, sondern ein 1 px hoher
 * Balken auf `-bottom-px`: er landet auf genau der Zeile, die der
 * `border-transparent` des Kopfes belegt. Gemessen, nicht gerechnet.
 *
 * Ihre Farbe ist `--border` (in der Schale `--jw-schale-kante`), also derselbe
 * Token wie jede andere Trennkante der Huelle. Sie ist damit BEWUSST keine
 * 3 : 1-Spur: `kanteHeben` in `lib/themes.ts` hebt jedes `border`-Token auf
 * `KANTE_MIN` (1,5 : 1) und keinen Deut weiter. Eine Linie, die kraeftiger
 * waere als die Trennlinie 12 px weiter rechts, waere genau das, was hier
 * NICHT gewollt ist — der Wunsch lautete „so wie wir's im Content-Bereich
 * haben".
 *
 * ── DER MASSSTAB IST IMMER 1 : 1 — UND DAS IST DER GANZE TRICK ──────────────
 *
 * `preserveAspectRatio="… slice"` skaliert mit
 * `max(Breite/viewBox-Breite, Hoehe/viewBox-Hoehe)`. Die viewBox der breiten
 * Fassung ist deshalb **320 breit**, nicht 176: die Leiste laesst sich von 8 rem
 * bis 20 rem ziehen (`LEISTE_MIN_REM`/`LEISTE_MAX_REM` in `Sidebar.tsx`), also
 * 128 bis 320 px — und bei einer viewBox-Breite von 320 ist der Faktor in JEDER
 * dieser Breiten genau 1. Daraus folgt dreierlei:
 *
 *  1. **Oben und unten wird nichts abgeschnitten.** Mit einer viewBox von 176
 *     stand der Faktor bei einer auf 300 px gezogenen Leiste bei 1,70; sichtbar
 *     blieb nur das Band y = 2,9 … 11,1 von 14.
 *  2. **Die Strichstaerke bleibt stehen.** Vorher wurde die Zier mit der Leiste
 *     mitfett — 1,1 px bei 176, 1,9 px bei 300. Ein Ornament aus Haarlinien
 *     haelt seine Haarlinien.
 *  3. **Eine viewBox-Einheit IST ein Bildpunkt.** Deshalb liegen alle
 *     waagerechten Zuege auf halben Werten (7,5 statt 7): ein 1,5 breiter
 *     Strich um 7,5 deckt Zeile 7 VOLL und laesst darueber und darunter je ein
 *     Viertel. Um 7,0 deckt derselbe Strich zwei Zeilen zu je 80 % — er ist
 *     dann nirgends ganz da und liest sich entsprechend blass.
 *
 * ── KONTRAST: DIE HIERARCHIE HAENGT AM STRICH, NICHT AN DER DECKKRAFT ───────
 *
 * Bis 0.459.0 trugen die Pfade `opacity` von 0,55 bis 0,12 auf
 * `text-muted-foreground`. Das war zum dritten Mal dieselbe Falle: der Ton ist
 * je Flaeche auf 4,5 : 1 GESUCHT (`ebene()` in `lib/themes.ts`), und jeder
 * Deckkraft-Faktor multipliziert sich damit weg. Bei 0,55 lagen **258 von 258**
 * Theme/Modus/Schalen-Zeilen unter 3 : 1, der schlechteste bei 2,03 (janeway
 * hell, „umklammert").
 *
 * **Warum es ausgerechnet unter „umklammert" am meisten auffaellt**, obwohl die
 * Zahl kaum kleiner ist als unter „flach" (2,03 gegen 2,25): dort kippt die
 * POLARITAET. Die Schale ist in beiden Modi dunkel, die Zier steht also hell
 * auf dunkel statt dunkel auf hell — und eine Haarlinie verliert dabei rund die
 * Haelfte ihrer wahrgenommenen Staerke, was das WCAG-Verhaeltnis nicht abbildet
 * (APCA, derselbe Messpunkt: Lc 42 unter „flach" gegen Lc 23 unter
 * „umklammert"). Betreiber, 2026-09-19: „beim klammernden Theme ist der
 * Kontrast so beschissen, dass ich's so gut wie gar nicht sehe."
 *
 * DIE ZIELMARKE: **die Hauptspur haelt 3 : 1 in jeder Schale, jedem Modus und
 * jedem der 43 Themes.** 3 : 1 ist der Wert, den WCAG 2.1 in 1.4.11 fuer
 * grafische Objekte setzt, und den diese Bibliothek ohnehin fuehrt — `ZIEL.ring`
 * in `lib/themes.ts` ist dieselbe Zahl. Die Zier ist `aria-hidden` und traegt
 * keine Auskunft, ist also nicht an 1.4.11 GEBUNDEN; aber „sichtbar, ohne
 * hineinzuzoomen" ist genau das, was diese Zahl beschreibt. Der Auslauf darf
 * verblassen, faellt aber nicht unter `KANTE_MIN` (1,5 : 1).
 *
 * Die drei Stufen und ihre gemessenen Minima stehen an `STUFEN`.
 *
 * ── WIE SIE GEBAUT IST ──────────────────────────────────────────────────────
 *
 * `fill="none"` steht als ATTRIBUT und wird hier nicht per CSS gesetzt — eine
 * Regel schlaegt das Attribut, und dann laufen die Pfade voll. In dieser
 * Bibliothek gibt es keine solche Regel (geprueft 2026-09-19); wer eine
 * einfuehrt, bricht das hier.
 *
 * Die Farbe kommt aus `currentColor` auf `text-muted-foreground`. Das ist der
 * einzige Ton, der in ALLEN DREI Schalen je Flaeche gesucht ist: unter
 * „umklammert" definiert `globals.css` ihn in der Leiste auf
 * `--jw-schale-gedaempft` um, unter „flach" und „schwebend" bleibt er der des
 * Themes auf `--background`. `--foreground` waere staerker, ist aber in der
 * schwebenden Leiste bei „Flaechen = eins" auf `--jw-tafel-vordergrund`
 * umdefiniert, waehrend die Flaeche darunter `--background` bleibt — dort also
 * kein verlaesslicher Partner.
 *
 * `aria-hidden`: Zier. Sie sagt nichts, was nicht schon dasteht.
 */
// Typ und Liste stehen in `zier-varianten.ts` (ohne "use client"): der Server liest die Liste in
// `liesHuelle`, und aus einer Client-Datei bekaeme er statt eines Arrays eine Client-Referenz.
export type { ZierVariante } from "./zier-varianten";

/**
 * Drei Stufen, jede mit einer gemessenen Untergrenze — und die Hierarchie
 * liegt zuerst im STRICH, dann erst in der Deckkraft.
 *
 * Die Minima sind ueber 43 Themes x 2 Modi x 3 Schalen gerechnet (258 Zeilen,
 * 2026-09-19; `--jw-schale-gedaempft` auf `--jw-schale` unter „umklammert",
 * sonst `muted-foreground` auf `--background`). Der schlechteste Fall ist in
 * allen drei Stufen derselbe: janeway hell unter „umklammert".
 *
 *                     gerechnet   gemessen (Bildpunkt)
 *   HAUPT    0,85  →   3,50 : 1     3,50 : 1   (Ziel 3,0 — WCAG 1.4.11, `ZIEL.ring`)
 *   NEBEN    0,62  →   2,33 : 1     2,22 : 1   (Ziel 2,0 — sichtbar, nachgeordnet)
 *   AUSLAUF  0,46  →   1,74 : 1     1,59 : 1   (Ziel 1,5 — `KANTE_MIN`, `themes.ts`)
 *
 * Die rechte Spalte ist der schlechteste Kernpunkt ueber alle vier gezeichneten
 * Varianten x 3 Schalen x 2 Modi x eingeklappt/ausgeklappt (61 Zeilen,
 * 2026-09-19), per Differenzbild gemessen: ein Schuss mit versteckten Pfaden
 * ist der reine Grund, der zweite die Zier, und gezaehlt wird nur, was sich
 * unterscheidet. Die beiden unteren Minima stammen BEIDE aus der eingeklappten
 * „Naht" — ein kurzer SCHRAEGER Strich deckt nirgends eine ganze Zeile und
 * verliert damit mehr an die Kantenglaettung als jede Waagerechte. Alle drei
 * Zielmarken sind gehalten, die Naht aber mit dem duennsten Rand; wer ihre
 * Stiche kuerzer macht, misst sie neu.
 *
 * ZWEI SACHEN, DIE DER PUFFER ZUR RECHNUNG NICHT DECKT:
 *  · Eine SENKRECHTE auf ganzzahligem x verliert genauso wie eine Waagerechte
 *    auf ganzzahligem y — die alte Variante „Raster" kam bei x = 68 auf 1,22
 *    und bei x = 68,5 auf 1,74. Halbe Werte gelten in BEIDEN Achsen.
 *  · Eine KURVE liegt nirgends auf einer ganzen Zeile und braucht deshalb mehr
 *    Strich fuer dieselbe Farbe. Die geloeschte Variante „Faden" kam mit den
 *    Stufenstaerken auf 1,87 und 1,35; mit 1,5 px durchgehend auf 2,30 und
 *    1,71. Deshalb zeichnet „Ranke" ihre Kurven mit der VOLLEN Staerke und
 *    stuft nur ueber die Deckkraft ab.
 *
 * Wer eine Zahl senkt, misst sie neu. `leistenzier.test.tsx` haelt fest, dass
 * KEIN Pfad eine Deckkraft ausserhalb dieser drei traegt — genau so ist die
 * 0,12 hier hereingekommen.
 */
const STUFEN = {
  haupt: { opacity: 0.85, strich: 1.5 },
  neben: { opacity: 0.62, strich: 1.1 },
  auslauf: { opacity: 0.46, strich: 1 },
} as const;

/** Die Mittellinie. Halber Wert, damit ein Strich eine Bildpunktzeile VOLL deckt. */
const MITTE = 7.5;

/**
 * Die Breite der eingeklappten Leiste (`--jw-leiste-schmal`, 3,5 rem).
 *
 * Sie ist zugleich die viewBox-Breite der schmalen Fassung und die Schwelle,
 * unter der umgeschaltet wird (s. „EINGEKLAPPT" unten).
 */
const SCHMAL = 56;

export interface LeistenzierProps {
  variante?: ZierVariante;
  className?: string;
}

/**
 * ── EINGEKLAPPT: EINE EIGENE, KURZE FASSUNG JE VARIANTE ─────────────────────
 *
 * Betreiber, 2026-09-19 (zum zweiten Mal): „Die Schaltkreislinien, die wir
 * unter dem Logo anzeigen, sehen im zusammengeklappten Zustand irgendwie
 * komisch aus."
 *
 * Beim ersten Anlauf war die Antwort eine Entwurfsregel: Massstab 1 heisst,
 * dass die ersten 56 Einheiten in beiden Zustaenden dieselben Bildpunkte sind
 * — also legt jede Variante ihre Figur dorthin. Das traegt nicht, und der
 * Grund steht in der Messung (2026-09-19, „umklammert" hell, Differenzbild,
 * gezaehlt werden die vom jeweiligen Zug gemalten Bildpunkte):
 *
 *     Variante      Zuege sichtbar   Bildpunkte in den letzten 2 Spalten
 *     leiterbahn        1 von 3                  6   durchgeschnitten
 *     ranke             1 von 3                  4   durchgeschnitten
 *     schichten         2 von 3                  2
 *     naht              2 von 3                  0
 *
 * Zwei Befunde, und beide sind keine Geschmacksfrage:
 *
 *  1. **Die Abstufung, die diese Zier ausmacht, existiert eingeklappt nicht.**
 *     Der ganze Entwurf ist eine Hierarchie aus drei gemessenen Stufen; in
 *     56 Einheiten kommen eine oder zwei davon ueberhaupt zum Zeichnen. Was
 *     man sieht, ist nicht „weniger von der Spur", sondern ein Strich.
 *  2. **Die Spur wird abgeschnitten, statt auszulaufen.** Genau die Aussage,
 *     die sie von einer Linie unterscheidet („sie hoert auf"), faellt weg: sie
 *     hoert nicht auf, sie wird gekappt.
 *
 * Dazu kommt die Geometrie der eingeklappten Leiste: dort ist ALLES eine
 * Spalte mittiger Zeichen — das Glyph ist 28 breit und sitzt mit je 14 px Luft
 * in der Mitte, die Navigationssymbole ebenso. Die Zier war das einzige
 * Element, das von Rand zu Rand lief, und ihr Blickfang lag daneben: die Via
 * der Leiterbahn sitzt bei x = 40, die Mitte ist 28.
 *
 * Deshalb jetzt: **eine eigene Fassung je Variante, mittig, mit Luft an beiden
 * Seiten und auslaufenden Enden.** Eingeklappt ist kein schmalerer Ausschnitt
 * derselben Aussage, sondern eine andere Komposition — eine Marke statt einer
 * Spur. „Gar keine Zier" waere die dritte vertretbare Antwort und ist bewusst
 * nicht genommen: der Betreiber hat „komisch" gesagt, nicht „weg damit", und
 * eingeklappt ist die Leiste in den meisten Apps der Normalfall.
 *
 * ── WIE UMGESCHALTET WIRD: CONTAINER-ANFRAGE, KEIN JAVASCRIPT ───────────────
 *
 * Die Komponente erfaehrt die Leistenbreite nicht — `Sidebar` reicht sie nicht
 * durch, und ein `ResizeObserver` fuer ein Ornament ist zu viel. Ein
 * `@container` um beide Fassungen loest das ohne eine einzige Zeile Zustand:
 * die Breite des Wrappers IST die Leistenbreite, und `@max-[80px]:` fragt
 * genau sie. 80 liegt sauber zwischen den beiden einzigen Breiten, die
 * vorkommen — 56 eingeklappt und ab 128 (`LEISTE_MIN_REM` = 8 rem) gezogen.
 *
 * **Der Ausfall waere still und sieht deshalb nach nichts aus:** generiert
 * Tailwind die beiden Varianten nicht, bleibt die schmale Fassung `hidden` und
 * die breite sichtbar — also exakt der Bestand von vorher, ohne Fehler und
 * ohne Warnung. Am 2026-09-19 am Bildpunkt gegengeprueft (eingeklappt zeichnet
 * die schmale Fassung, ausgeklappt die breite); wer hier etwas aendert, misst
 * das nach und verlaesst sich nicht auf `getComputedStyle`.
 */
export function Leistenzier({ variante = "leiterbahn", className }: LeistenzierProps) {
  if (variante === "linie") return <Kantenlinie className={className} />;
  const { breit: Breit, schmal: Schmal } = ZEICHNUNGEN[variante];
  return (
    /* `aria-hidden` sitzt auf der HUELLE, nicht nur auf den beiden SVGs: sonst
       bliebe ein leerer Knoten im Baum, den ein Vorleser betritt, um darin
       nichts zu finden. */
    <span
      aria-hidden
      data-jw-zier={variante}
      className="@container pointer-events-none block w-full"
    >
      <svg
        aria-hidden
        focusable="false"
        data-jw-zier-fassung="breit"
        viewBox="0 0 320 14"
        preserveAspectRatio="xMinYMid slice"
        className={cn("block h-3.5 w-full text-muted-foreground @max-[80px]:hidden", className)}
      >
        <Breit />
      </svg>
      {/* `xMidYMid`, nicht `xMinYMid`: die schmale Figur ist mittig gebaut und
          soll mittig bleiben. Der Massstab ist auch hier 1 — die Leiste ist
          56 breit, das SVG durch den Rand der Leiste 55, und `slice` nimmt
          davon `max(55/56, 14/14) = 1`. */}
      <svg
        aria-hidden
        focusable="false"
        data-jw-zier-fassung="schmal"
        viewBox={`0 0 ${SCHMAL} 14`}
        preserveAspectRatio="xMidYMid slice"
        className={cn("hidden h-3.5 w-full text-muted-foreground @max-[80px]:block", className)}
      >
        <Schmal />
      </svg>
    </span>
  );
}

/**
 * Die Wahl „normale Linie" — ein Balken auf der Kantenzeile des Kopfblocks,
 * kein SVG-Pfad. Das Warum und die drei gemessenen Kantenhoehen stehen im
 * Dateikopf.
 *
 * `absolute`: sie positioniert sich am `absolute inset-x-0 bottom-0`, in das
 * `Sidebar` die Zier haengt. Ausserhalb dieses Rahmens ist die Variante
 * sinnlos — eine Kante ohne Kopf, an die sie sich legt, gibt es nicht.
 */
function Kantenlinie({ className }: { className?: string }) {
  return (
    <span
      aria-hidden
      data-jw-zier="linie"
      className={cn(
        "pointer-events-none absolute inset-x-0 -bottom-px block h-px bg-border",
        className,
      )}
    />
  );
}

/** Eine Gruppe in einer der drei Stufen. Spart je Pfad zwei Attribute und
 *  macht im Diff sichtbar, WELCHE Stufe jemand anfasst. */
function Zug({
  stufe,
  children,
}: {
  stufe: keyof typeof STUFEN;
  children: React.ReactNode;
}) {
  return (
    <g
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={STUFEN[stufe].strich}
      opacity={STUFEN[stufe].opacity}
    >
      {children}
    </g>
  );
}

/**
 * LEITERBAHN — der Entwurf, den der Betreiber beschrieben hat: „was dann so
 * auseinandergeht und dann mit so Kreisen, was vielleicht ein Loch in der Mitte
 * hat." Und sein Favorit unter den sieben (2026-09-19: „das Einzige, was ich
 * aktuell so ein bisschen verstehe"). **Die breite Fassung ist deshalb
 * unveraendert** — was gefaellt, faesst man nicht an.
 *
 * Gebaut wie eine echte Platine: Knicke mit **45 Grad**, nie rechtwinklig. Das
 * ist nicht Zierde, sondern der Grund, warum eine Leiterbahn wie eine
 * Leiterbahn aussieht — rechte Winkel gelten im Leiterplatten-Entwurf als
 * Fehler (Aetzfallen an der Innenecke).
 *
 * Die Durchkontaktierung ist ein Ring mit Loch (`fill="none"`), nicht ein
 * Punkt: das Loch ist das, was eine Via ausmacht. Der zweite Abzweig endet in
 * einem Pad ohne Loch: zwei Enden, zwei Bedeutungen.
 */
function Leiterbahn() {
  return (
    <>
      <Zug stufe="haupt">
        {/* Aus dem Rand, ein 45°-Versatz nach oben, bis an den Ring der Via. */}
        <path d="M0 7.5h26l3-3h8" />
        {/* Die Durchkontaktierung: Ring und Loch. */}
        <circle cx="40" cy="4.5" r="2.6" strokeWidth={STUFEN.neben.strich} />
        <circle cx="40" cy="4.5" r="0.9" strokeWidth={0.8} />
        {/* Auf der anderen Seite wieder heraus und zurueck auf die Mittellinie.
            Sie endet bei 86 und damit GENAU am zweiten Abzweig: eine Bahn, die
            ueber ihre Abzweigung hinaus noch ein Stueck weiterlaeuft und dann
            aufhoert, ist im Leiterplatten-Entwurf ein Stub — und im Bild ein
            Faden, der an nichts endet. */}
        <path d="M43 4.5h2l3 3h38" />
      </Zug>
      <Zug stufe="neben">
        {/* Abzweig nach unten, endet in einem Pad ohne Loch. */}
        <path d="M74 7.5l3 3h30" />
        <circle cx="109.5" cy="10.5" r="2" />
        {/* Zweiter Abzweig nach oben — er setzt bei 86 an, wo die Hauptbahn
            endet, und fuehrt sie fort. Im ersten Entwurf begann er bei 95 und
            damit HINTER ihrem Ende: im Bild ein Strich mit einem Knick, der an
            nichts anschloss (2026-09-19 am Schuss gesehen, nicht am Code). */}
        <path d="M86 7.5l3-3h51" />
      </Zug>
      <Zug stufe="auslauf">
        {/* Auslauf: duenner, blasser — aber nicht unter `KANTE_MIN`. */}
        <path d="M140 4.5h44l3 3h56l3-3h74" />
      </Zug>
    </>
  );
}

/**
 * LEITERBAHN eingeklappt — die Via in der Mitte, weil die Via die einzige
 * abgeschlossene Aussage dieses Entwurfs ist: ein Loch in einer Platine sagt
 * „hier geht es durch", ganz ohne Fortsetzung.
 *
 * Spiegelbildlich um x = 28 gebaut, also genau unter der Mitte des Glyphs. Die
 * Bahn kommt von links unten, geht durch die Via und verlaesst sie nach rechts
 * oben — in Summe die 45-Grad-Sprache der breiten Fassung auf 40 Einheiten.
 * Beide Enden laufen im AUSLAUF aus und stossen nicht an den Rand: die Figur
 * verblasst, statt gekappt zu werden.
 */
function LeiterbahnSchmal() {
  return (
    <>
      <Zug stufe="haupt">
        <path d="M12 10.5h4l3-3h6.4" />
        <circle cx="28" cy="7.5" r="2.6" strokeWidth={STUFEN.neben.strich} />
        <circle cx="28" cy="7.5" r="0.9" strokeWidth={0.8} />
        <path d="M30.6 7.5h6.4l3-3h4" />
      </Zug>
      <Zug stufe="auslauf">
        <path d="M8 10.5h4M44 4.5h4" />
      </Zug>
    </>
  );
}

/**
 * SCHICHTEN — das Flaechenmodell dieser Huelle im Schnitt: Schale (y 10,5),
 * Tafel (7,5), Karte (4,5), in genau der Reihenfolge, in der `globals.css` sie
 * stapelt, und mit genau der Gewichtung, die sie auf dem Schirm haben. Die
 * Schale traegt alles und ist der staerkste Strich; die Karte schwebt oben und
 * ist der duennste.
 *
 * **Was sich am 2026-09-19 geaendert hat und warum:** vorher war es eine
 * Treppe — jede Stufe begann dort, wo die vorige endete, und man sah die drei
 * Ebenen nie gleichzeitig. Ein Schnitt zeigt aber gerade den STAPEL. Jetzt
 * laeuft jede Ebene ueber die naechste hinaus, sodass es einen Bereich gibt
 * (58 … 96), in dem alle drei uebereinander liegen; der 45-Grad-Anstieg zeigt,
 * dass die obere aus der unteren waechst und nicht neben ihr liegt.
 */
function Schichten() {
  return (
    <>
      <Zug stufe="haupt">
        <path d="M0 10.5h96" />
      </Zug>
      <Zug stufe="neben">
        <path d="M23 10.5l3-3h140" />
      </Zug>
      <Zug stufe="auslauf">
        <path d="M55 7.5l3-3h262" />
      </Zug>
    </>
  );
}

/**
 * SCHICHTEN eingeklappt — der Stapel selbst, ohne Anlauf: drei Platten, je
 * 26 Einheiten lang, um 7 gegeneinander versetzt. Das ist dieselbe Aussage in
 * ihrer kuerzesten Form, und sie braucht keinen Rand: 8 … 48, mittig um 28.
 */
function SchichtenSchmal() {
  return (
    <>
      <Zug stufe="haupt">
        <path d="M8 10.5h26" />
      </Zug>
      <Zug stufe="neben">
        <path d="M15 7.5h26" />
      </Zug>
      <Zug stufe="auslauf">
        <path d="M22 4.5h26" />
      </Zug>
    </>
  );
}

/**
 * NAHT — die stillste, und die einzige ohne durchgehende Linie. Betreiber,
 * 2026-09-19: „geile Idee, kann man aber auch noch optimieren."
 *
 * Fuer Arbeitsbereiche, in denen den ganzen Tag gelesen wird: es gibt nichts
 * Waagerechtes, dem das Auge folgen koennte. Eine Naht haelt zwei Stuecke
 * zusammen, ohne sich als Kante auszugeben — das ist „Spur statt Linie" in
 * seiner schaerfsten Form.
 *
 * Der Takt ist nicht ausgedacht: die Abstaende folgen `deckel * tanh(i/deckel)`
 * aus `huellen-auftritt.ts` — derselbe Verlauf, mit dem sich die Huelle beim
 * Oeffnen aufbaut. Gleicher Anfangstakt, gleiches Budget, kein Sprung: die
 * Stiche stehen links dicht und laufen nach rechts gleichmaessig aus, ohne dass
 * irgendwo eine Kante im Rhythmus sitzt (die haette `Math.min(i, deckel)`).
 *
 * **Das Optimieren war die NEIGUNG, nicht der Takt.** Vorher standen alle
 * Stiche parallel und gleich hoch auf der Mittellinie — in 14 px liest sich das
 * als Reihe von Schraegstrichen, also als Zaehlmarken. Ein wirklicher Stich
 * wechselt die Seite: er kommt oben heraus und unten wieder herein. Deshalb
 * wechseln sich jetzt Neigung UND Seite ab (`seite` in `stich`), und aus den
 * Zaehlmarken wird eine Zickzacknaht — ohne dass eine waagerechte Linie
 * entsteht, die das Auge fuehren koennte.
 */
const NAHT_STICHE = (() => {
  const deckel = 7;
  const striche: { x: number; laenge: number }[] = [];
  let x = 2.5;
  for (let i = 0; i < 30 && x < 320; i++) {
    striche.push({ x, laenge: 5.4 - 3.4 * Math.tanh(i / deckel) });
    x += 6 + 11 * Math.tanh(i / deckel);
  }
  return striche;
})();

/**
 * Ein Stich: geneigt, damit er als Stich liest und nicht als Skalenstrich —
 * und je nach `seite` oberhalb oder unterhalb der Naht, mit gespiegelter
 * Neigung. `seite` ist +1 oder -1.
 */
function stich({ x, laenge }: { x: number; laenge: number }, seite: number) {
  const halb = laenge / 2;
  const neigung = laenge * 0.45 * seite;
  const y = MITTE + seite * 0.9;
  return `M${(x - neigung / 2).toFixed(1)} ${(y + halb).toFixed(1)}l${neigung.toFixed(1)} ${(-laenge).toFixed(1)}`;
}

function naehte(von: number, bis: number) {
  return NAHT_STICHE.slice(von, bis)
    .map((s, i) => stich(s, (von + i) % 2 === 0 ? 1 : -1))
    .join("");
}

function Naht() {
  return (
    <>
      <Zug stufe="haupt">
        <path d={naehte(0, 4)} />
      </Zug>
      <Zug stufe="neben">
        <path d={naehte(4, 9)} />
      </Zug>
      <Zug stufe="auslauf">
        <path d={naehte(9, NAHT_STICHE.length)} />
      </Zug>
    </>
  );
}

/**
 * NAHT eingeklappt — fuenf Stiche um die Mitte, nach BEIDEN Seiten
 * ausblassend. Die breite Fassung laeuft nach rechts aus, weil sie eine Spur
 * ist; die schmale ist eine Marke und hat keine Richtung, also verblasst sie
 * symmetrisch.
 */
const NAHT_SCHMAL: { x: number; laenge: number; seite: number; stufe: keyof typeof STUFEN }[] = [
  { x: 10, laenge: 3.4, seite: 1, stufe: "auslauf" },
  { x: 19, laenge: 4.4, seite: -1, stufe: "neben" },
  { x: 28, laenge: 5.4, seite: 1, stufe: "haupt" },
  { x: 37, laenge: 4.4, seite: -1, stufe: "neben" },
  { x: 46, laenge: 3.4, seite: 1, stufe: "auslauf" },
];

function NahtSchmal() {
  return (
    <>
      {(["haupt", "neben", "auslauf"] as const).map((stufe) => (
        <Zug key={stufe} stufe={stufe}>
          <path
            d={NAHT_SCHMAL.filter((s) => s.stufe === stufe)
              .map((s) => stich(s, s.seite))
              .join("")}
          />
        </Zug>
      ))}
    </>
  );
}

/**
 * Ein Blatt als LINSE: zwei Boegen vom Knoten zur Spitze, nach beiden Seiten
 * gleich weit ausgebaucht.
 *
 * Vorher standen die Boegen als Handarbeit im Pfad, und sie lagen zu dicht
 * beieinander: in 14 px las sich das als Splitter, nicht als Blatt (Betreiber,
 * 2026-09-19 zur Ranke: „koennte schoener sein"). Als Funktion ist der Bauch
 * eine Zahl, die man sehen kann.
 *
 * **Und die Zahl ist groesser, als die Rechnung nahelegt.** Bei Bauch 2,1 ist
 * die Linse rechnerisch 4,2 Einheiten dick — zwei Striche zu 1,5 fressen davon
 * aber 1,5, und was uebrig bleibt, ist kein Loch, sondern ein Grat. Am
 * 2026-09-19 acht Fassungen nebeneinander gerendert und bei neunfacher
 * Vergroesserung angesehen: erst ab Laenge ~14 und Bauch ~3,0 liest sich die
 * Linse als Umriss und nicht als Splitter. Ein duennerer Strich loest es auch,
 * nimmt dem Blatt aber die Farbe — und damit die Stufe, die es tragen soll.
 *
 * **UNTER dem Stengel ist dafuer kein Platz.** Zwischen der Mittellinie und dem
 * unteren Rand liegen 3,4 Einheiten; eine Linse mit Bauch 3 passt dort nicht,
 * und eine flachere ist wieder der Splitter von vorher. Die Blaetter zeigen
 * deshalb alle nach OBEN und wechseln stattdessen die RICHTUNG (zurueck, vor,
 * vor) — das ist der Wechsel, den man bei dieser Hoehe sieht.
 *
 * `strokeLinejoin` muss am Pfad `miter` sein: die beiden Boegen treffen sich an
 * einer SPITZE, und `round` (der Vorgabewert der Gruppe) macht daraus eine
 * Schlaufe.
 */
function blatt(nx: number, ny: number, tx: number, ty: number, bauch: number) {
  const mx = (nx + tx) / 2;
  const my = (ny + ty) / 2;
  const dx = tx - nx;
  const dy = ty - ny;
  const l = Math.hypot(dx, dy);
  const px = (-dy / l) * bauch;
  const py = (dx / l) * bauch;
  return (
    `M${nx} ${ny}Q${(mx + px).toFixed(1)} ${(my + py).toFixed(1)} ${tx} ${ty}` +
    `Q${(mx - px).toFixed(1)} ${(my - py).toFixed(1)} ${nx} ${ny}Z`
  );
}

/**
 * RANKE — der Gegenpol, und bewusst die unauffaelligste ihrer Art. Der
 * Betreiber hat „etwas Rosiges, das nach Blumen aussieht" als Gegenpol genannt
 * und am 2026-09-19 nachgeschoben: „die Ranke koennte schoener sein, find ich
 * […] mach mal schoener."
 *
 * Drei Dinge sind daraufhin anders, und jedes hat einen Grund:
 *
 *  1. **Die Blaetter sind Linsen mit Bauch** (`blatt()`), nicht mehr zwei
 *     handgesetzte Boegen. Vorher lagen die Boegen so dicht, dass in 14 px ein
 *     Splitter herauskam.
 *  2. **Es gibt eine Wickelranke.** Eine Ranke OHNE Ranke war der eigentliche
 *     Mangel: das Wort bezeichnet genau dieses eingerollte Fadenende, mit dem
 *     sich die Pflanze festhaelt. Die Spirale ist aus Viertelbogen-Kubiken mit
 *     schrumpfendem Radius gebaut (3,9 → 0,74, Faktor 0,66), also eine echte
 *     logarithmische Einrollung und kein Kringel.
 *  3. **Der Stengel wellt sich deutlicher** und die Blaetter wechseln ihre
 *     Richtung (zurueck, vor, vor) statt ihre Seite — warum nicht die Seite,
 *     steht an `blatt()`.
 *
 * **Und alle drei Stufen liegen jetzt VOR x = 175.** Die viewBox ist 320 breit,
 * weil die Leiste so weit gezogen werden kann — die VORGABE sind aber 176 px,
 * und was dahinter liegt, sieht ohne Ziehen niemand. Der Auslauf begann bei
 * 176 und zeichnete am 2026-09-19 im Differenzbild **null Bildpunkte**: die
 * Variante hatte in der Normalbreite zwei Stufen statt drei, ohne dass es
 * auffiel. Er beginnt jetzt bei 132, die Knospe sitzt bei 150. Wer eine neue
 * Variante baut, prueft das genauso — die Messung zaehlt die Punkte je Zug.
 *
 * Keine Blueten, keine Fuellung — eine gefuellte Blume in einer Werkzeugspalte
 * ist ein Aufkleber, kein Ornament. Und alle Kurven tragen die VOLLE
 * Strichstaerke (1,5), abgestuft wird allein ueber die Deckkraft: eine Kurve
 * liegt nirgends auf einer ganzen Bildpunktzeile, also kauft nur der Strich die
 * Farbe (Messung an `STUFEN`).
 */
function Ranke() {
  const voll = STUFEN.haupt.strich;
  return (
    <>
      <Zug stufe="haupt">
        <path d="M0 8.7c14 0 20-2.1 34-2.1s18 2.1 30 2.1" />
        <path d={blatt(26, 6.9, 12, 3.2, 3)} strokeLinejoin="miter" />
      </Zug>
      <Zug stufe="neben">
        <path d="M64 8.7c16 0 22 1.7 38 1.7s16-1.9 30-1.9" strokeWidth={voll} />
        <path d={blatt(86, 9.8, 99, 6.4, 2.9)} strokeWidth={voll} strokeLinejoin="miter" />
        {/* Die Wickelranke. Ihr Anfang (112,56 | 10,23) liegt auf dem Stengel —
            sie waechst aus ihm heraus und haengt nicht daneben. */}
        <path
          d="M112.56 10.23C114.57 11.02 115.87 8.87 116.39 7.55C116.92 6.23 115.5 5.37 114.63 5.02C113.75 4.68 113.19 5.61 112.96 6.19C112.73 6.76 113.35 7.14 113.73 7.29"
          strokeWidth={voll}
        />
      </Zug>
      <Zug stufe="auslauf">
        <path d="M132 8.5c22 0 32-.9 54-.9s34 .8 62 .8s38-.5 72-.5" strokeWidth={voll} />
        {/* Knospe am Auslauf: oben spitz, unten voll — kein Ring. */}
        <path
          d="M150.5 2.6c2 1.8 3 3.8 2.6 5.5-.5 1.9-2.4 2.7-3.9 1.8-1.6-1-1.9-3.4-.4-5.4.5-.7 1-1.3 1.7-1.9Z"
          strokeWidth={voll}
          strokeLinejoin="miter"
        />
        <path d={blatt(190, 7.6, 204, 4.2, 2.7)} strokeWidth={voll} strokeLinejoin="miter" />
      </Zug>
    </>
  );
}

/**
 * RANKE eingeklappt — ein Trieb: Stengel, ein Blatt, und die Wickelranke, die
 * ihn abschliesst. Genau die drei Dinge, die den Entwurf ausmachen, auf
 * 32 Einheiten um die Mitte.
 *
 * Die Spirale ist derselbe Bau wie in der breiten Fassung, nur kleiner
 * (3,3 → 0,55, Faktor 0,64) — und sie sitzt rechts, weil eine Ranke sich an
 * ihrem ENDE einrollt.
 */
function RankeSchmal() {
  const voll = STUFEN.haupt.strich;
  return (
    <>
      <Zug stufe="haupt">
        <path d="M11.5 9.6c9 0 13-1.9 22-1.9" />
        <path d={blatt(27, 8.1, 14, 4.3, 2.7)} strokeLinejoin="miter" />
      </Zug>
      <Zug stufe="neben">
        <path d="M33.5 7.7c2.6 0 4.7 0.9 6.8 2.3" strokeWidth={voll} />
        <path
          d="M40.29 9.97C41.98 10.64 43.03 8.76 43.46 7.68C43.89 6.59 42.69 5.92 42 5.64C41.3 5.37 40.87 6.14 40.7 6.58C40.52 7.03 41.01 7.3 41.3 7.41"
          strokeWidth={voll}
        />
      </Zug>
    </>
  );
}

const ZEICHNUNGEN: Record<
  Exclude<ZierVariante, "linie">,
  { breit: () => React.ReactElement; schmal: () => React.ReactElement }
> = {
  leiterbahn: { breit: Leiterbahn, schmal: LeiterbahnSchmal },
  ranke: { breit: Ranke, schmal: RankeSchmal },
  naht: { breit: Naht, schmal: NahtSchmal },
  schichten: { breit: Schichten, schmal: SchichtenSchmal },
};

/** Nur fuer den Waechter: die Schwelle und die Stufen sind Entwurfsgroessen und
 *  keine Formatierung — wer sie aendert, aendert eine gemessene Zusage. */
export const ZIER_SCHMAL_BREITE = SCHMAL;
export const ZIER_STUFEN = STUFEN;
